import { useEffect, useRef } from "react";
import { Heart, Minus, Plus, ShoppingBag, Trash2, X } from "lucide-react";
import { formatMoney } from "../../../lib/money";
import { cartCount, cartSubtotal, lineKey, useCart, useShop, useWishlist } from "../../../stores/cart";
import { useStorefrontContext } from "./StorefrontContext";

/** Wishlist and cart icons for the site header. */
export function HeaderActions() {
  const { slug, interactive } = useStorefrontContext();
  const count = cartCount(useCart(slug));
  const openCart = useShop((s) => s.setDrawerOpen);
  return (
    <div className="zs-header-actions">
      <button
        type="button"
        className="zs-icon-btn"
        aria-label={`Cart, ${count} items`}
        onClick={() => interactive && openCart(true)}
      >
        <ShoppingBag size={20} aria-hidden="true" />
        {count > 0 ? <span className="zs-badge">{count}</span> : null}
      </button>
      <CartDrawer />
    </div>
  );
}

function CartLines() {
  const { store, slug } = useStorefrontContext();
  const lines = useCart(slug);
  const setQuantity = useShop((s) => s.setQuantity);
  const remove = useShop((s) => s.remove);
  return (
    <ul className="zs-lines">
      {lines.map((line) => {
        const key = lineKey(line);
        const options = Object.entries(line.options);
        return (
          <li key={key} className="zs-line">
            <div className="zs-line__media">{line.image ? <img src={line.image} alt="" /> : null}</div>
            <div className="zs-line__body">
              <div className="zs-line__top">
                <strong>{line.name}</strong>
                <span>{formatMoney(line.priceMinor * line.quantity, store.currency)}</span>
              </div>
              {options.length > 0 ? (
                <span className="zs-line__opts">{options.map(([k, v]) => `${k}: ${v}`).join(" · ")}</span>
              ) : null}
              <div className="zs-line__controls">
                <div className="zs-qty zs-qty--sm">
                  <button
                    type="button"
                    aria-label={`Decrease ${line.name}`}
                    onClick={() => setQuantity(slug, key, line.quantity - 1)}
                  >
                    <Minus size={12} aria-hidden="true" />
                  </button>
                  <span>{line.quantity}</span>
                  <button
                    type="button"
                    aria-label={`Increase ${line.name}`}
                    disabled={line.quantity >= 99}
                    onClick={() => setQuantity(slug, key, line.quantity + 1)}
                  >
                    <Plus size={12} aria-hidden="true" />
                  </button>
                </div>
                <button
                  type="button"
                  className="zs-link-btn"
                  aria-label={`Remove ${line.name}`}
                  onClick={() => remove(slug, key)}
                >
                  <Trash2 size={14} aria-hidden="true" />
                  Remove
                </button>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/** Slide-in cart. Closes on Escape and on the backdrop. */
export function CartDrawer() {
  const { store, slug, paths, go } = useStorefrontContext();
  const open = useShop((s) => s.drawerOpen);
  const setOpen = useShop((s) => s.setDrawerOpen);
  const lines = useCart(slug);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    panelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, setOpen]);

  if (!open) return null;
  const subtotal = cartSubtotal(lines);

  return (
    <div className="zs-drawer" role="presentation">
      <div className="zs-drawer__backdrop" onClick={() => setOpen(false)} />
      <div
        ref={panelRef}
        className="zs-drawer__panel"
        role="dialog"
        aria-modal="true"
        aria-label="Your cart"
        tabIndex={-1}
      >
        <div className="zs-drawer__head">
          <h2 className="zs-subtitle">Your cart</h2>
          <button type="button" className="zs-icon-btn" aria-label="Close cart" onClick={() => setOpen(false)}>
            <X size={20} aria-hidden="true" />
          </button>
        </div>
        {lines.length === 0 ? (
          <div className="zs-empty zs-drawer__empty">
            <ShoppingBag size={32} aria-hidden="true" />
            <p>Your cart is empty.</p>
            <button
              type="button"
              className="zs-btn"
              onClick={() => {
                setOpen(false);
                go(paths.shop);
              }}
            >
              Start shopping
            </button>
          </div>
        ) : (
          <>
            <div className="zs-drawer__body">
              <CartLines />
            </div>
            <div className="zs-drawer__foot">
              <div className="zs-sum-row zs-sum-row--total">
                <span>Subtotal</span>
                <span>{formatMoney(subtotal, store.currency)}</span>
              </div>
              <p className="zs-muted">Delivery is calculated at checkout.</p>
              <button
                type="button"
                className="zs-btn zs-btn--block"
                onClick={() => {
                  setOpen(false);
                  go(paths.checkout);
                }}
              >
                Checkout
              </button>
              <button type="button" className="zs-btn zs-btn--ghost zs-btn--block" onClick={() => setOpen(false)}>
                Continue shopping
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
